import { useEffect, useRef, useState } from "react";
import { ChatStatus } from "@/constants/enums";

type Status = ChatStatus;

type UseSSEOptions = {
  endpoint?: string;
  aiImportId?: string | number;
  enabled?: boolean;
  getAuthToken?: () => string | null | undefined;
  lastEventId?: string | null | undefined;
};

const BACKEND_URL = import.meta.env.VITE_BACKEND_URL as string;

/**
 * Streams Server-Sent Events over fetch() by reading the response body.
 *
 * Connects to GET /aiImport/stream with query param aiImportId and
 * Authorization header set to `Bearer {token}`.
 *
 * @param opts Optional configuration: endpoint, aiImportId (default 50), enabled flag, custom token getter
 * @returns SSE connection status, last chunk, all chunks, and error state
 */
export function useSSE(opts?: UseSSEOptions) {

  const endpoint = opts?.endpoint ?? `${BACKEND_URL}/aiImport/stream`;
  const aiImportId = opts?.aiImportId;
  const enabled = opts?.enabled !== false;
  const lastEventId = opts?.lastEventId;

  const [status, setStatus] = useState<Status>(ChatStatus.CLOSED);
  const [lastMessage, setLastMessage] = useState<string | null>(null);
  const [messages, setMessages] = useState<string[]>([]);
  const [error, setError] = useState<string | null>(null);

  const abortRef = useRef<AbortController | null>(null);
  const readerRef = useRef<ReadableStreamDefaultReader<string> | null>(null);
  const closeTimeoutRef = useRef<number | null>(null);
  // Capture the initial lastEventId only once to avoid reconnecting on every update
  const initialSinceRef = useRef<string | null | undefined>(lastEventId);

  useEffect(() => {
    if (!enabled || !aiImportId) {
      setStatus(ChatStatus.CLOSED);
      return;
    }

    const controller = new AbortController();
    abortRef.current = controller;
    let isActive = true;

    async function connect() {
      try {
        // cancel any pending close transitions
        if (closeTimeoutRef.current != null) {
          clearTimeout(closeTimeoutRef.current);
          closeTimeoutRef.current = null;
        }
        setStatus(ChatStatus.CONNECTING);
        setError(null);

        const rawToken = opts?.getAuthToken?.() ?? sessionStorage.getItem("token") ?? "";
        const authHeader = rawToken?.startsWith("Bearer ") ? rawToken : `Bearer ${rawToken}`;
        let url = `${endpoint}?aiImportId=${encodeURIComponent(String(aiImportId))}`;
        // Only add `since` param from initial value; don't reconnect on subsequent updates
        if (initialSinceRef.current) {
          url += `&since=${encodeURIComponent(String(initialSinceRef.current))}`;
        }
        const response = await fetch(url, {
          method: "GET",
          headers: {
            Accept: "text/event-stream",
            Authorization: authHeader,
          },
          signal: controller.signal,
        });

        if (!response.ok) {
          throw new Error(`SSE request failed (${response.status})`);
        }

        if (!response.body) {
          throw new Error("ReadableStream not supported in this browser.");
        }

        const reader = response.body
          .pipeThrough(new TextDecoderStream())
          .getReader();
        readerRef.current = reader as unknown as ReadableStreamDefaultReader<string>;

        setStatus(ChatStatus.OPEN);

        // Read raw chunks as they arrive
        while (isActive) {
          const { value, done } = await reader.read();
          if (done) break;
          if (value == null) continue;
          setLastMessage(value);
          setMessages((prev) => [...prev, value]);
        }
      } catch (e: any) {
        if (!controller.signal.aborted) {
          setStatus(ChatStatus.ERROR);
          setError(e?.message ?? "Unknown SSE error");
        }
      } finally {
        // Smooth the transition to "closed" to avoid StrictMode flicker
        if (closeTimeoutRef.current != null) {
          clearTimeout(closeTimeoutRef.current);
        }
        closeTimeoutRef.current = window.setTimeout(() => {
          setStatus((prev) => (prev === ChatStatus.ERROR ? ChatStatus.ERROR : ChatStatus.CLOSED));
          closeTimeoutRef.current = null;
        }, 200);
        try {
          await readerRef.current?.cancel?.();
        } catch (e: any) {
          // AbortError is expected when the fetch/stream was aborted via AbortController.
          // Suppress it to avoid noisy logs during normal teardown.
          if (e?.name !== "AbortError") {
            // Keep non-abort errors visible for debugging
            console.warn("Error canceling reader:", e);
          }
        }
        readerRef.current = null;
      }
    }

    connect();

    return () => {
      isActive = false;
      abortRef.current?.abort();
      abortRef.current = null;
      if (closeTimeoutRef.current != null) {
        clearTimeout(closeTimeoutRef.current);
        closeTimeoutRef.current = null;
      }
    };
  }, [endpoint, aiImportId, enabled]);

  return { status, lastMessage, messages, error };
}
